import { useState } from 'react';
import { assessmentData } from '../data/lessonsData';

function SelfAssessment() {
  const [answers, setAnswers] = useState<{ [key: number]: number }>({});
  const [showResults, setShowResults] = useState(false);

  const handleAnswer = (questionId: number, answerIndex: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: answerIndex
    }));
  };

  const handleSubmit = () => {
    if (Object.keys(answers).length === assessmentData.questions.length) {
      setShowResults(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const calculateScore = () => {
    let correct = 0;
    assessmentData.questions.forEach(q => {
      if (answers[q.id] === q.correctAnswer) {
        correct++;
      }
    });
    return correct;
  };

  const score = calculateScore();
  const percentage = Math.round((score / assessmentData.questions.length) * 100);

  const getScoreMessage = () => {
    if (percentage >= 80) {
      return {
        title: "¡Excelente Dominio!",
        message: "Has demostrado una comprensión sólida de las metodologías de entrenamiento. Estás listo para diseñar programas efectivos.",
        icon: "ri-trophy-line",
        color: "text-[#39FF14]"
      };
    } else if (percentage >= 60) {
      return {
        title: "Buen Progreso",
        message: "Tienes una base sólida, pero revisa las lecciones donde fallaste para consolidar tu conocimiento.",
        icon: "ri-thumb-up-line",
        color: "text-blue-500"
      };
    } else {
      return {
        title: "Necesitas Repasar",
        message: "Te recomendamos revisar el contenido del módulo antes de continuar. La comprensión de estos conceptos es fundamental.",
        icon: "ri-book-open-line",
        color: "text-orange-500"
      };
    }
  };

  const scoreMessage = getScoreMessage();

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-8">
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#39FF14] rounded-full mb-4">
          <i className="ri-quiz-line text-3xl text-[#1A3A2E]"></i>
        </div>
        <h1 className="text-3xl font-bold text-[#1A3A2E] mb-4">Autoevaluación</h1>
        <p className="text-gray-600 max-w-2xl mx-auto">
          Evalúa tu comprensión del módulo con estas 5 preguntas. Selecciona la respuesta correcta para cada una.
        </p>
      </div>

      {showResults && (
        <div className={`mb-8 bg-gradient-to-br from-[#1A3A2E] to-[#0F2419] rounded-xl p-8 text-center`}>
          <i className={`${scoreMessage.icon} text-6xl ${scoreMessage.color} mb-4`}></i>
          <h2 className="text-3xl font-bold text-white mb-2">{scoreMessage.title}</h2>
          <p className="text-xl text-gray-300 mb-4">
            Puntuación: {score}/{assessmentData.questions.length} ({percentage}%)
          </p>
          <p className="text-gray-300 max-w-2xl mx-auto">
            {scoreMessage.message}
          </p>
        </div>
      )}

      <div className="space-y-8">
        {assessmentData.questions.map((question, index) => {
          const userAnswer = answers[question.id];
          const isAnswered = userAnswer !== undefined;
          const isCorrect = userAnswer === question.correctAnswer;

          return (
            <div key={question.id} className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start gap-3 mb-4">
                <span className="flex-shrink-0 w-8 h-8 bg-[#1A3A2E] text-white rounded-full flex items-center justify-center font-bold text-sm">
                  {index + 1}
                </span>
                <p className="text-gray-800 font-semibold flex-1">{question.question}</p>
              </div>

              <div className="space-y-3 ml-11">
                {question.options.map((option, optionIndex) => {
                  const isSelected = userAnswer === optionIndex;
                  const isCorrectOption = optionIndex === question.correctAnswer;
                  
                  let optionClass = "bg-white border-2 border-gray-200 text-gray-700";
                  
                  if (showResults) {
                    if (isCorrectOption) {
                      optionClass = "bg-green-50 border-2 border-green-500 text-green-900";
                    } else if (isSelected && !isCorrect) {
                      optionClass = "bg-red-50 border-2 border-red-500 text-red-900";
                    }
                  } else if (isSelected) {
                    optionClass = "bg-[#39FF14]/20 border-2 border-[#39FF14] text-[#1A3A2E]";
                  }

                  return (
                    <button
                      key={optionIndex}
                      onClick={() => !showResults && handleAnswer(question.id, optionIndex)}
                      disabled={showResults}
                      className={`w-full text-left p-4 rounded-lg transition-all cursor-pointer ${optionClass} ${
                        !showResults ? 'hover:border-[#39FF14]' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full border-2 flex items-center justify-center text-xs font-bold">
                          {String.fromCharCode(65 + optionIndex)}
                        </span>
                        <span className="text-sm flex-1">{option}</span>
                        {showResults && isCorrectOption && (
                          <i className="ri-check-line text-green-600 text-xl"></i>
                        )}
                        {showResults && isSelected && !isCorrect && (
                          <i className="ri-close-line text-red-600 text-xl"></i>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              {showResults && (
                <div className="mt-4 ml-11 p-4 bg-blue-50 border-l-4 border-blue-500 rounded">
                  <p className="text-sm text-blue-900">
                    <strong>Explicación:</strong> {question.explanation}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!showResults && (
        <div className="mt-8 text-center">
          <button
            onClick={handleSubmit}
            disabled={Object.keys(answers).length !== assessmentData.questions.length}
            className={`px-8 py-4 rounded-lg font-bold text-lg transition-all whitespace-nowrap ${
              Object.keys(answers).length === assessmentData.questions.length
                ? 'bg-[#39FF14] text-[#1A3A2E] hover:bg-[#2de00f] cursor-pointer'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {Object.keys(answers).length === assessmentData.questions.length
              ? 'Ver Resultados'
              : `Responde todas las preguntas (${Object.keys(answers).length}/${assessmentData.questions.length})`}
          </button>
        </div>
      )}

      {showResults && (
        <div className="mt-8 text-center">
          <button
            onClick={() => {
              setAnswers({});
              setShowResults(false);
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            className="px-8 py-4 bg-[#1A3A2E] text-white rounded-lg font-bold hover:bg-[#0F2419] transition-colors whitespace-nowrap cursor-pointer"
          >
            Reintentar Evaluación
          </button>
        </div>
      )}
    </div>
  );
}

export default SelfAssessment;